/**
 * Association deadlines keep statutory notification time separate from the
 * provider's voluntary review target. Callers pass a date-only awareness fact
 * so the calculation cannot be shifted by a browser or server timezone.
 */
import type { ComplianceDeadlineItem, ComplianceDeadlineStatus } from './types';
import type { ComplianceDeadlineSettings } from '../compliance-settings';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const COMMISSION_NOTICE_PROVISION = 'Aged Care Act 2024 s 167; Rules r 167-55';
const REVIEW_TARGET_LABEL = 'Statura operational target';
export const ASSOCIATION_COMMISSION_NOTICE = {
  id: 'ASSOCIATION_COMMISSION_NOTICE',
  tier: 'T1',
  kind: 'STATUTORY',
  provision: COMMISSION_NOTICE_PROVISION,
  daysFromAwareness: 14,
} as const;

/** Annual is Statura's operational target, not an asserted statutory cadence. */
export const ASSOCIATION_REVIEW = {
  id: 'ASSOCIATION_REVIEW',
  tier: 'T2',
  kind: 'TARGET',
  label: REVIEW_TARGET_LABEL,
  defaultCadenceDays: 365,
  maximumCadenceDays: 365,
} as const;

function parseDateOnly(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error('A date-only value is required.');
  }

  const parsed = new Date(`${value}T00:00:00.000Z`);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(parsed);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (Number.isNaN(parsed.getTime()) || `${year}-${month}-${day}` !== value) {
    throw new Error('A valid date-only value is required.');
  }

  return parsed;
}

function dateOnlyAfterDays(value: string, days: number): string {
  const date = parseDateOnly(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toLocaleDateString('en-CA', { timeZone: 'UTC' });
}

/** A Commission notice is due exactly 14 calendar days after awareness. */
export function calculateAssociationCommissionNoticeDueOn(awareOn: string): string {
  return dateOnlyAfterDays(awareOn, ASSOCIATION_COMMISSION_NOTICE.daysFromAwareness);
}

/** A review may only be tightened below the annual Statura operational target. */
export function calculateAssociationReviewDueOn(
  reviewedOn: string,
  cadenceDays?: number,
  settings?: ComplianceDeadlineSettings,
): string {
  const effectiveCadenceDays =
    cadenceDays ??
    settings?.association_review_cadence_days ??
    ASSOCIATION_REVIEW.defaultCadenceDays;
  if (
    !Number.isInteger(effectiveCadenceDays) ||
    effectiveCadenceDays < 1 ||
    effectiveCadenceDays > 365
  ) {
    throw new Error('Association review cadence must be between 1 and 365 days.');
  }

  return dateOnlyAfterDays(reviewedOn, effectiveCadenceDays);
}

export interface AssociationCommissionNoticeRow {
  id: string;
  associationId: string;
  eventKind: 'START' | 'VARY' | 'CEASE';
  awareOn: string;
  dueOn: string;
  status: 'OPEN' | 'NOTIFIED' | 'NOT_REQUIRED';
}

export interface AssociationReviewRow {
  id: string;
  nextReviewDueOn: string | null;
}

function statusForDateOnly(dueOn: string, now: Date): ComplianceDeadlineStatus {
  const dueMs = parseDateOnly(dueOn).getTime();
  const daysUntil = (dueMs - now.getTime()) / MS_PER_DAY;
  if (daysUntil < 0) return 'MISSED';
  if (daysUntil <= 14) return 'DUE_SOON';
  return 'UPCOMING';
}

/**
 * The Commission notice remains visible until the controlled workflow records
 * the external lodgement. `dueOn` is read from SQL's generated `aware_on +
 * 14` column; the calculator only classifies that authoritative clock.
 */
export function evaluateAssociationCommissionNotice(
  row: AssociationCommissionNoticeRow,
  now: Date,
): { item: ComplianceDeadlineItem } | null {
  if (row.status !== 'OPEN') return null;

  return {
    item: {
      id: `association-commission-notice:${row.id}`,
      register: 'ASSOCIATION_COMMISSION_NOTICE',
      recordRef: row.id,
      title: `Associated-provider ${row.eventKind.toLowerCase()} notice to the Commission`,
      dueAt: row.dueOn,
      kind: 'STATUTORY',
      provisionOrTargetLabel: COMMISSION_NOTICE_PROVISION,
      status: statusForDateOnly(row.dueOn, now),
      href: '/settings/registration-providers',
      meta: { associationId: row.associationId, awareOn: row.awareOn, eventKind: row.eventKind },
    },
  };
}

/**
 * Review dates are provider targets, never recast as a statutory deadline.
 * They become due soon in the same two-week horizon used for the notice clock.
 */
export function evaluateAssociationReview(
  row: AssociationReviewRow,
  now: Date,
): { item: ComplianceDeadlineItem } | null {
  if (!row.nextReviewDueOn) return null;

  return {
    item: {
      id: `association-review:${row.id}`,
      register: 'ASSOCIATION_REVIEW',
      recordRef: row.id,
      title: 'Associated-provider arrangement review',
      dueAt: row.nextReviewDueOn,
      kind: 'TARGET',
      provisionOrTargetLabel: REVIEW_TARGET_LABEL,
      status: statusForDateOnly(row.nextReviewDueOn, now),
      href: '/settings/registration-providers',
    },
  };
}
